/**
 * Money math in integer agorot (1 ₪ = 100 agorot) to avoid floating point errors.
 * DB columns are numeric(14,2); convert at the edges with toAgorot / fromAgorot.
 */
export type Agorot = number;

export function toAgorot(value: number | string | null | undefined): Agorot {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) throw new Error(`Invalid money value: ${value}`);
  return Math.round(n * 100);
}

export function fromAgorot(a: Agorot): number {
  return Math.round(a) / 100;
}

/** Format as "₪1,234.50" in he-IL. */
export function formatILS(value: number | string | null | undefined, opts: { decimals?: boolean } = {}): string {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0);
  const decimals = opts.decimals ?? true;
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).format(Number.isFinite(n) ? n : 0);
}

export interface LineInput {
  quantity: number;
  unitPrice: number; // ₪ ex VAT, after customer pricing
  discountPct?: number; // extra line discount 0..100
}

export interface LineResult {
  gross: Agorot; // qty * unit
  discount: Agorot;
  net: Agorot;
}

/** Round half away from zero, to the nearest agora. */
function roundAgorot(x: number): Agorot {
  return Math.sign(x) * Math.round(Math.abs(x));
}

export function calcLine(line: LineInput): LineResult {
  if (line.quantity < 0) throw new Error('Quantity must be ≥ 0');
  const unit = toAgorot(line.unitPrice);
  const gross = roundAgorot(unit * line.quantity);
  const pct = Math.min(100, Math.max(0, line.discountPct ?? 0));
  const discount = roundAgorot((gross * pct) / 100);
  return { gross, discount, net: gross - discount };
}

export interface TotalsInput {
  lines: LineInput[];
  shipping?: number; // ₪ ex VAT
  documentDiscountPct?: number; // whole-document discount on the lines' net
  vatRate: number; // e.g. 0.18
}

export interface Totals {
  subtotal: number; // sum of line gross
  lineDiscounts: number;
  documentDiscount: number;
  discountTotal: number;
  shipping: number;
  taxable: number;
  vatAmount: number;
  total: number;
  lines: { gross: number; discount: number; net: number }[];
}

/** Quote/order totals: lines → document discount → + shipping → VAT on everything → total (VAT inclusive). */
export function calcTotals(input: TotalsInput): Totals {
  const lines = input.lines.map(calcLine);
  const subtotal = lines.reduce((s, l) => s + l.gross, 0);
  const lineDiscounts = lines.reduce((s, l) => s + l.discount, 0);
  const netLines = subtotal - lineDiscounts;
  const docPct = Math.min(100, Math.max(0, input.documentDiscountPct ?? 0));
  const documentDiscount = roundAgorot((netLines * docPct) / 100);
  const shipping = toAgorot(input.shipping ?? 0);
  const taxable = netLines - documentDiscount + shipping;
  const vat = roundAgorot(taxable * input.vatRate);
  return {
    subtotal: fromAgorot(subtotal),
    lineDiscounts: fromAgorot(lineDiscounts),
    documentDiscount: fromAgorot(documentDiscount),
    discountTotal: fromAgorot(lineDiscounts + documentDiscount),
    shipping: fromAgorot(shipping),
    taxable: fromAgorot(taxable),
    vatAmount: fromAgorot(vat),
    total: fromAgorot(taxable + vat),
    lines: lines.map((l) => ({ gross: fromAgorot(l.gross), discount: fromAgorot(l.discount), net: fromAgorot(l.net) })),
  };
}
